import { useState } from 'react';

import { ArrowLeft, CheckCircle2, Loader2, Save, Sparkles, Tag as TagIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Segmented } from '@/components/feedback/Segmented';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';
import {
  useCreateMisclassifiedCall,
  useMisclassifiedCallDetail,
  useUpdateMisclassifiedCall,
} from '@/hooks/useMisclassifiedCalls';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useQualityConfigList } from '@/hooks/useQualityConfigs';
import { callsApi } from '@/lib/api/calls.api';
import { parseSchemaFields, type TaxonomyField } from '@/lib/taxonomy';
import { cn } from '@/lib/utils';

import type { FeedbackTargetType } from '@/lib/api/misclassified-calls.api';

type TriState = 'ignore' | 'true' | 'false';

// ── small presentational helpers ────────────────────────────────────────────

const TriToggle = ({ value, onChange }: { value: TriState; onChange: (s: TriState) => void }) => {
  const opts: { key: TriState; label: string; active: string }[] = [
    { key: 'ignore', label: 'Ignore', active: 'bg-background text-foreground shadow-sm' },
    { key: 'true', label: 'True', active: 'bg-emerald-500 text-white shadow-sm' },
    { key: 'false', label: 'False', active: 'bg-rose-500 text-white shadow-sm' },
  ];
  return (
    <div className="bg-muted inline-flex rounded-lg p-0.5">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-medium transition-colors',
            value === o.key ? o.active : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

const CurrentBool = ({ v }: { v: unknown }) => {
  if (v === undefined || v === null)
    return <span className="text-muted-foreground text-xs">not run</span>;
  const val = Boolean(v);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        val ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', val ? 'bg-emerald-500' : 'bg-rose-500')} />
      {String(val)}
    </span>
  );
};

const fmtCurrent = (v: unknown): string => {
  if (v === undefined) return 'not run';
  if (v === null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// ── page ─────────────────────────────────────────────────────────────────────

export const MisclassifiedCallEditPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = !routeId || routeId === 'new';

  usePageTitle(isNew ? 'Add Misclassified Call' : 'Misclassified Call');

  const { data: detail, isLoading } = useMisclassifiedCallDetail(isNew ? undefined : routeId);
  const { data: classifierConfigs } = useClassifierConfigList();
  const { data: qualityConfigs } = useQualityConfigList();
  const createMutation = useCreateMisclassifiedCall();
  const updateMutation = useUpdateMisclassifiedCall();

  const [targetType, setTargetType] = useState<FeedbackTargetType>('classifier');
  const [callId, setCallId] = useState('');
  const [targetConfigId, setTargetConfigId] = useState('');
  const [configAutoDetected, setConfigAutoDetected] = useState(false);
  const [tagsText, setTagsText] = useState('');
  const [reason, setReason] = useState('');
  const [expectedLabels, setExpectedLabels] = useState<Record<string, boolean>>({});
  const [expectedData, setExpectedData] = useState<Record<string, unknown>>({});
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({});
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState<{
    labels: Record<string, unknown>;
    data: Record<string, unknown>;
  } | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const configList = (targetType === 'quality' ? qualityConfigs : classifierConfigs) ?? [];
  const selectedConfig = configList.find((c) => c.id === targetConfigId);
  const schema =
    selectedConfig && 'taxonomy' in selectedConfig
      ? selectedConfig.taxonomy
      : selectedConfig && 'rubric' in selectedConfig
        ? selectedConfig.rubric
        : undefined;
  const fields = parseSchemaFields(schema);
  const labelFields = fields.filter((f) => f.type === 'boolean');
  const dataFields = fields.filter((f) => f.type !== 'boolean');
  const labelNoun = targetType === 'quality' ? 'flags' : 'labels';
  const setCount = Object.keys(expectedLabels).length + Object.keys(expectedData).length;

  // Default the config to the DB default when creating / switching type.
  const defaultConfigId = (configList.find((c) => c.is_default) ?? configList[0])?.id;
  const [prevKey, setPrevKey] = useState('');
  const key = `${targetType}:${configList.length}`;
  if (isNew && key !== prevKey) {
    setPrevKey(key);
    if (!targetConfigId && defaultConfigId) setTargetConfigId(defaultConfigId);
  }

  const [prevDetail, setPrevDetail] = useState(detail);
  if (detail !== prevDetail) {
    setPrevDetail(detail);
    if (detail) {
      setTargetType(detail.target_type);
      setCallId(detail.call_id);
      setTargetConfigId(detail.target_config_id ?? '');
      setTagsText(detail.tags.join(', '));
      setReason(detail.reason ?? '');
      setExpectedLabels(detail.expected_labels ?? {});
      setExpectedData(detail.expected_data ?? {});
      void loadCurrent(detail.call_id, detail.target_type, false);
    }
  }

  /**
   * Fetch the call's current agent output. When `autoSelectConfig` is on (the
   * user pressed Load), also select the exact config that produced that run, so
   * the schema and "current" values line up with what actually ran.
   */
  async function loadCurrent(id: string, type: FeedbackTargetType, autoSelectConfig: boolean) {
    if (!id) return;
    setLoadingCurrent(true);
    try {
      const runs = await callsApi.listAgentRuns(id);
      if (type === 'quality') {
        const canon = runs.qualities.find((q) => q.name === 'quality') ?? runs.qualities[0];
        setCurrent(canon ? { labels: canon.flags, data: canon.data } : null);
        if (autoSelectConfig && canon?.quality_config_id) {
          setTargetConfigId(canon.quality_config_id);
          setConfigAutoDetected(true);
        }
      } else {
        const canon =
          runs.classifications.find((c) => c.name === 'classification') ?? runs.classifications[0];
        setCurrent(canon ? { labels: canon.labels, data: canon.data } : null);
        if (autoSelectConfig && canon?.classifier_config_id) {
          setTargetConfigId(canon.classifier_config_id);
          setConfigAutoDetected(true);
        }
      }
      setLoaded(true);
    } catch {
      setCurrent(null);
      setLoaded(true);
    } finally {
      setLoadingCurrent(false);
    }
  }

  const changeTargetType = (type: FeedbackTargetType) => {
    setTargetType(type);
    setTargetConfigId('');
    setConfigAutoDetected(false);
    setExpectedLabels({});
    setExpectedData({});
    setJsonDrafts({});
    setJsonErrors({});
    setPrevKey('');
    if (callId.trim()) void loadCurrent(callId.trim(), type, true);
  };

  const setTri = (fieldKey: string, state: TriState) =>
    setExpectedLabels((prev) => {
      const next = { ...prev };
      if (state === 'ignore') delete next[fieldKey];
      else next[fieldKey] = state === 'true';
      return next;
    });

  const dataAsserted = (fieldKey: string) => fieldKey in expectedData;

  const toggleDataAssert = (field: TaxonomyField, on: boolean) => {
    setExpectedData((prev) => {
      const next = { ...prev };
      if (!on) {
        delete next[field.key];
        return next;
      }
      if (field.type === 'integer' || field.type === 'number') next[field.key] = 0;
      else if (field.type === 'enum') next[field.key] = field.enumValues?.[0] ?? '';
      else if (field.type === 'object') next[field.key] = {};
      else if (field.type === 'array') next[field.key] = [];
      else next[field.key] = '';
      return next;
    });
    if (!on) {
      setJsonDrafts((p) => {
        const n = { ...p };
        delete n[field.key];
        return n;
      });
      setJsonErrors((p) => {
        const n = { ...p };
        delete n[field.key];
        return n;
      });
    } else if (field.type === 'object') {
      setJsonDrafts((p) => ({ ...p, [field.key]: '{}' }));
    } else if (field.type === 'array') {
      setJsonDrafts((p) => ({ ...p, [field.key]: '[]' }));
    }
  };

  const setPrimitive = (fieldKey: string, value: unknown) =>
    setExpectedData((prev) => ({ ...prev, [fieldKey]: value }));

  const setJsonDraft = (fieldKey: string, text: string) => {
    setJsonDrafts((p) => ({ ...p, [fieldKey]: text }));
    try {
      const parsed = JSON.parse(text);
      setExpectedData((prev) => ({ ...prev, [fieldKey]: parsed }));
      setJsonErrors((p) => {
        const n = { ...p };
        delete n[fieldKey];
        return n;
      });
    } catch (err) {
      setJsonErrors((p) => ({
        ...p,
        [fieldKey]: err instanceof Error ? err.message : 'Invalid JSON',
      }));
    }
  };

  const handleSave = async () => {
    const cid = callId.trim();
    if (!cid) {
      toast({ variant: 'destructive', title: 'Missing call', description: 'Enter a call ID.' });
      return;
    }
    if (Object.keys(jsonErrors).length > 0) {
      toast({
        variant: 'destructive',
        title: 'Invalid JSON',
        description: 'Fix the highlighted fields.',
      });
      return;
    }
    const tags = tagsText
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const payload = {
      target_type: targetType,
      reason: reason || null,
      expected_labels: expectedLabels,
      expected_data: expectedData,
      target_config_id: targetConfigId || null,
      tags,
    };
    try {
      if (isNew) {
        await createMutation.mutateAsync({ call_id: cid, ...payload });
        toast({ variant: 'success', title: 'Added', description: 'Call added to registry.' });
      } else {
        await updateMutation.mutateAsync({ id: routeId, data: payload });
        toast({ variant: 'success', title: 'Saved', description: 'Registry entry updated.' });
      }
      navigate('/misclassified-calls');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast({ variant: 'destructive', title: 'Save failed', description: msg });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isBusy = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="bg-muted/20 min-h-full">
      <div className="mx-auto max-w-4xl space-y-5 p-6">
        {/* Header */}
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground -ml-2 h-8"
            onClick={() => navigate('/misclassified-calls')}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Misclassified calls
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isNew ? 'Add misclassified call' : 'Edit misclassified call'}
          </h1>
          <p className="text-muted-foreground text-sm">
            Load the call&apos;s current output, then record the correct {labelNoun} and data. Tag
            it so judge tuning can pick it up.
          </p>
        </div>

        {/* 1 · Source */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                1
              </span>
              Source
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Agent</label>
                <div>
                  <Segmented<FeedbackTargetType>
                    value={targetType}
                    onChange={changeTargetType}
                    disabled={!isNew}
                    options={[
                      { value: 'classifier', label: 'Classifier' },
                      { value: 'quality', label: 'Quality' },
                    ]}
                  />
                </div>
              </div>

              <div className="min-w-[280px] flex-1 space-y-1.5">
                <label className="text-sm font-medium">Call ID</label>
                <div className="flex gap-2">
                  <Input
                    value={callId}
                    onChange={(e) => {
                      setCallId(e.target.value);
                      setLoaded(false);
                    }}
                    placeholder="Paste the call UUID…"
                    className="font-mono text-xs"
                    disabled={!isNew}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && loadCurrent(callId.trim(), targetType, true)
                    }
                  />
                  <Button
                    variant="secondary"
                    onClick={() => loadCurrent(callId.trim(), targetType, true)}
                    disabled={!callId.trim()}
                  >
                    {loadingCurrent ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="mr-1.5 h-4 w-4" /> Load
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {targetType === 'quality' ? 'Rubric' : 'Taxonomy'} config
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={targetConfigId}
                  onValueChange={(v) => {
                    setTargetConfigId(v);
                    setConfigAutoDetected(false);
                  }}
                >
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select a config…" />
                  </SelectTrigger>
                  <SelectContent>
                    {configList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                        {c.is_default ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {configAutoDetected && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Detected from this call
                  </span>
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                Auto-selected from the call&apos;s run when you press Load. Change it only if you
                want to grade against a different config.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 2 · Correct output */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                2
              </span>
              Correct output
              {setCount > 0 && (
                <span className="bg-primary/10 text-primary ml-1 rounded-full px-2 py-0.5 text-xs font-semibold">
                  {setCount} set
                </span>
              )}
            </CardTitle>
            <p className="text-muted-foreground pl-8 text-sm">
              Correct only what&apos;s wrong — leave the rest on <b>Ignore</b>. The chip shows the
              agent&apos;s current answer.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {!selectedConfig && (
              <p className="text-muted-foreground text-sm">Select a config to see its fields.</p>
            )}

            {/* Boolean labels/flags */}
            {labelFields.length > 0 && (
              <div>
                <div className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
                  {labelNoun}
                </div>
                <div className="divide-y rounded-lg border">
                  {labelFields.map((f) => {
                    const tri: TriState =
                      f.key in expectedLabels
                        ? expectedLabels[f.key]
                          ? 'true'
                          : 'false'
                        : 'ignore';
                    const isSet = f.key in expectedLabels;
                    return (
                      <div
                        key={f.key}
                        className={cn(
                          'flex items-center justify-between gap-4 px-3 py-2.5 transition-colors',
                          isSet ? 'bg-primary/[0.03]' : 'hover:bg-muted/40',
                        )}
                      >
                        <div className="min-w-0">
                          <div className="truncate font-mono text-sm">{f.key}</div>
                          {f.description && (
                            <div className="text-muted-foreground truncate text-xs">
                              {f.description}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-4">
                          <div className="text-muted-foreground hidden items-center gap-1.5 text-xs sm:flex">
                            now <CurrentBool v={current?.labels?.[f.key]} />
                          </div>
                          <TriToggle value={tri} onChange={(s) => setTri(f.key, s)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Non-boolean data */}
            {dataFields.length > 0 && (
              <div>
                <div className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
                  data
                </div>
                <div className="divide-y rounded-lg border">
                  {dataFields.map((f) => {
                    const on = dataAsserted(f.key);
                    return (
                      <div
                        key={f.key}
                        className={cn(
                          'px-3 py-2.5 transition-colors',
                          on ? 'bg-primary/[0.03]' : 'hover:bg-muted/40',
                        )}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <Switch checked={on} onCheckedChange={(v) => toggleDataAssert(f, v)} />
                            <div className="min-w-0">
                              <div className="truncate font-mono text-sm">
                                {f.key}
                                <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                                  {f.type}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
                            <span className="shrink-0">now</span>
                            <span
                              title={fmtCurrent(current?.data?.[f.key])}
                              className="text-foreground bg-muted block max-w-[220px] truncate rounded px-1.5 py-0.5 sm:max-w-[340px]"
                            >
                              {fmtCurrent(current?.data?.[f.key])}
                            </span>
                          </div>
                        </div>
                        {on && (
                          <div className="mt-2.5 pl-11">
                            {(f.type === 'integer' || f.type === 'number') && (
                              <Input
                                type="number"
                                value={String((expectedData[f.key] as number | string) ?? '')}
                                onChange={(e) =>
                                  setPrimitive(
                                    f.key,
                                    f.type === 'integer'
                                      ? parseInt(e.target.value || '0', 10)
                                      : Number(e.target.value || '0'),
                                  )
                                }
                                className="w-40"
                              />
                            )}
                            {f.type === 'enum' && (
                              <Select
                                value={String(expectedData[f.key] ?? '')}
                                onValueChange={(v) => setPrimitive(f.key, v)}
                              >
                                <SelectTrigger className="w-64">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {(f.enumValues ?? []).map((ev) => (
                                    <SelectItem key={String(ev)} value={String(ev)}>
                                      {String(ev)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {f.type === 'string' && (
                              <Input
                                value={String((expectedData[f.key] as string) ?? '')}
                                onChange={(e) => setPrimitive(f.key, e.target.value)}
                                className="max-w-md"
                              />
                            )}
                            {(f.type === 'object' || f.type === 'array') && (
                              <>
                                <textarea
                                  value={
                                    jsonDrafts[f.key] ??
                                    JSON.stringify(expectedData[f.key] ?? null, null, 2)
                                  }
                                  onChange={(e) => setJsonDraft(f.key, e.target.value)}
                                  rows={4}
                                  className="border-input bg-background w-full rounded-md border px-3 py-2 font-mono text-xs"
                                />
                                {jsonErrors[f.key] && (
                                  <p className="text-destructive mt-1 text-xs">
                                    {jsonErrors[f.key]}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3 · Labelling */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                3
              </span>
              Tags &amp; notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium">
                <TagIcon className="text-muted-foreground h-3.5 w-3.5" /> Tags
              </label>
              <Input
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="availability, relative-dates"
              />
              <p className="text-muted-foreground text-xs">
                Comma-separated. Group similar mistakes so a judge tuning run can select them
                together.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Why is the current output wrong?</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                placeholder="e.g. agent talked over the caller, but interrupted_caller came back false"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action bar */}
        <div className="bg-background sticky bottom-4 flex items-center justify-between gap-4 rounded-xl border px-4 py-3 shadow-lg">
          <div className="text-muted-foreground text-xs">
            {loaded && !current && callId.trim() ? (
              <span className="text-amber-600">No agent run found on this call yet.</span>
            ) : (
              <span>
                {setCount} correction{setCount === 1 ? '' : 's'} ·{' '}
                {tagsText.split(',').filter((t) => t.trim()).length} tag(s)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/misclassified-calls')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isBusy}>
              <Save className="mr-2 h-4 w-4" /> {isBusy ? 'Saving…' : 'Save call'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
